// Main-thread side of the analysis worker (analysis.worker.ts): promises for its three jobs.

import type { AutoProposal, ClaveEvidence, CountEvent, CountMap, HalfBeatDecision, TempoCurve } from '@salsacount/core';

/** What the tracker found. The count map is derived from `beats` and the event log on the main thread (`deriveMap`). */
export interface TrackResult {
  readonly beats: number[];
  /** Mean tempo over the song, beats per minute. */
  readonly bpm: number;
  readonly tempo: TempoCurve;
  readonly halfBeat: HalfBeatDecision | null;
  /** Which beats (by index parity) the phase model found to be the odd counts, or null (S9c; core's `Track.oddParity`): `deriveMap` puts count 1 on the odd beat nearest the taps. */
  readonly oddParity: 0 | 1 | null;
  /** Each tap in force against the grid the half-beat decision chose: song seconds of the tap and how far it is from its nearest beat, in milliseconds (positive: late). */
  readonly residuals: readonly { readonly t: number; readonly ms: number }[];
}

/** What `load` cost in the worker, milliseconds: the mono mix converted to the analysis rate, and the spectrum with both envelopes (S9e). */
export interface LoadTiming {
  readonly resampleMs: number;
  readonly envelopeMs: number;
}

/** What the worker's `propose` found (S9e): the proposal and the track seeded by it (one call, one lattice), or why it declined. `ms` is the worker's own time for the proposal and the track. */
export interface ProposeResult {
  readonly proposal: AutoProposal | null;
  readonly declined: string | null;
  readonly track: TrackResult | null;
  readonly ms: number;
}

export type Stage = 'envelope' | 'tracking';

export type WorkerIn =
  | { readonly type: 'load'; readonly req: number; readonly id: string; readonly samples: Float32Array; readonly sampleRate: number }
  | { readonly type: 'track'; readonly req: number; readonly id: string; readonly events: readonly CountEvent[]; readonly offsetS: number }
  | { readonly type: 'evidence'; readonly req: number; readonly id: string; readonly map: CountMap }
  | { readonly type: 'propose'; readonly req: number; readonly id: string; readonly offsetS: number }
  | { readonly type: 'forget'; readonly id: string };

export type WorkerOut =
  | { readonly type: 'progress'; readonly id: string; readonly stage: Stage; readonly fraction: number }
  | { readonly type: 'done'; readonly req: number; readonly result?: TrackResult | ClaveEvidence | ProposeResult | LoadTiming }
  | { readonly type: 'failed'; readonly req: number; readonly message: string };

export class Analysis {
  private readonly worker = new Worker(new URL('./analysis.worker.ts', import.meta.url), { type: 'module' });
  private readonly pending = new Map<number, { resolve(v: unknown): void; reject(e: Error): void }>();
  private seq = 0;

  constructor(private readonly onProgress: (id: string, stage: Stage, fraction: number) => void) {
    this.worker.onmessage = (e: MessageEvent<WorkerOut>) => {
      const m = e.data;
      if (m.type === 'progress') { this.onProgress(m.id, m.stage, m.fraction); return; }
      const p = this.pending.get(m.req);
      if (!p) return;
      this.pending.delete(m.req);
      if (m.type === 'done') p.resolve(m.result); else p.reject(new Error(m.message));
    };
    this.worker.onerror = (e) => {
      for (const p of this.pending.values()) p.reject(new Error(e.message || 'analysis worker failed'));
      this.pending.clear();
    };
  }

  private call<T>(msg: (req: number) => WorkerIn, transfer: Transferable[] = []): Promise<T> {
    const req = this.seq++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(req, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage(msg(req), transfer);
    });
  }

  /** Compute and keep the onset envelope of a track. Takes the samples (mono): they are moved to the worker. */
  load(id: string, samples: Float32Array, sampleRate: number): Promise<LoadTiming> {
    return this.call<LoadTiming>((req) => ({ type: 'load', req, id, samples, sampleRate }), [samples.buffer]);
  }

  /** Track the beats with these events' taps, on the kept envelope. */
  track(id: string, events: readonly CountEvent[], offsetS: number): Promise<TrackResult> {
    return this.call<TrackResult>((req) => ({ type: 'track', req, id, events, offsetS }));
  }

  /** The count from the audio alone (S9e), on the kept envelopes: `proposeCount` on the held lattice, and the tracker seeded by it. */
  propose(id: string, offsetS: number): Promise<ProposeResult> {
    return this.call<ProposeResult>((req) => ({ type: 'propose', req, id, offsetS }));
  }

  /** Clave evidence for a count map, on the kept envelope. */
  evidence(id: string, map: CountMap): Promise<ClaveEvidence> {
    return this.call<ClaveEvidence>((req) => ({ type: 'evidence', req, id, map }));
  }

  forget(id: string): void {
    this.worker.postMessage({ type: 'forget', id } satisfies WorkerIn);
  }
}
